import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Box,
  Text,
  Flex,
  Input,
  Select,
  Spinner,
  Badge,
  VStack,
  FormControl,
  FormLabel,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  IconButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, CheckCircle, Trash, Recycle, Warning } from 'phosphor-react';
import { useCompanyFields, ICreateFieldPayload, ICompanyField } from '../../hooks/useCompanyFields';

interface AddFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddFieldModal({ isOpen, onClose }: AddFieldModalProps) {
  const {
    getFields,
    createField,
    deleteField,
    deleteFieldCascade,
    listOrphanValues,
    listOrphanSets,
    clearOrphanValues,
    clearOrphanSets,
    clearAllOrphans,
  } = useCompanyFields();
  const queryClient = useQueryClient();
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<'select' | 'text'>('text');
  const [creatingField, setCreatingField] = useState<string | null>(null);
  const [deletingField, setDeletingField] = useState<string | null>(null);
  const [fieldToDelete, setFieldToDelete] = useState<ICompanyField | null>(null);
  const [isClearingValues, setIsClearingValues] = useState(false);
  const [isClearingSets, setIsClearingSets] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);

  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // Buscar campos existentes
  const {
    data: fieldsData,
    isLoading,
    refetch: refetchFields,
  } = useQuery({
    queryKey: ['companyFields'],
    queryFn: getFields,
    enabled: isOpen,
  });

  // Buscar órfãos
  const { data: orphanValuesData, isLoading: isLoadingOrphanValues } = useQuery({
    queryKey: ['orphanValues'],
    queryFn: listOrphanValues,
    enabled: isOpen,
  });

  const { data: orphanSetsData, isLoading: isLoadingOrphanSets } = useQuery({
    queryKey: ['orphanSets'],
    queryFn: listOrphanSets,
    enabled: isOpen,
  });

  const existingFields = fieldsData?.data ?? [];
  const orphanValues = Array.isArray(orphanValuesData?.data) ? orphanValuesData.data : [];
  const orphanSets = Array.isArray(orphanSetsData?.data) ? orphanSetsData.data : [];
  const totalOrphans = orphanValues.length + orphanSets.length;
  const isLoadingOrphans = isLoadingOrphanValues || isLoadingOrphanSets;

  // Limpar estado ao fechar o modal
  const handleClose = () => {
    setNewFieldName('');
    setNewFieldType('text');
    onClose();
  };

  // Mutation para criar campo
  const { mutateAsync: createFieldMutation } = useMutation({
    mutationFn: createField,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['companyFields'] });
      await queryClient.refetchQueries({ queryKey: ['CompaniesFilters'] });
      await queryClient.refetchQueries({ queryKey: ['orphanValues'] });
      await queryClient.refetchQueries({ queryKey: ['orphanSets'] });
    },
  });

  // Mutation para deletar campo (mantém valores)
  const { mutateAsync: deleteFieldMutation } = useMutation({
    mutationFn: deleteField,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['companyFields'] });
      await queryClient.refetchQueries({ queryKey: ['CompaniesFilters'] });
      await queryClient.refetchQueries({ queryKey: ['orphanValues'] });
      await queryClient.refetchQueries({ queryKey: ['orphanSets'] });
    },
  });

  // Mutation para deletar campo E valores (cascade)
  const { mutateAsync: deleteFieldCascadeMutation } = useMutation({
    mutationFn: deleteFieldCascade,
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['companyFields'] });
      await queryClient.refetchQueries({ queryKey: ['CompaniesFilters'] });
      await queryClient.refetchQueries({ queryKey: ['orphanValues'] });
      await queryClient.refetchQueries({ queryKey: ['orphanSets'] });
    },
  });

  const getErrorMessage = (error: any, defaultMessage: string): string => {
    const detail = error.response?.data?.detail;
    if (!detail) return defaultMessage;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    if (typeof detail === 'object' && detail.msg) return detail.msg;
    return defaultMessage;
  };

  const handleCreateField = async (fieldData: ICreateFieldPayload) => {
    // Refetch para garantir que temos os dados mais recentes antes de validar
    const { data: freshData } = await refetchFields();
    const currentFieldNames = (freshData?.data ?? []).map((f: ICompanyField) => f.campo);

    if (currentFieldNames.includes(fieldData.campo)) {
      toast.info(`O campo "${fieldData.campo}" já existe.`, {
        position: 'top-center',
        duration: 2000,
      });
      return;
    }

    setCreatingField(fieldData.campo);
    try {
      await createFieldMutation(fieldData);
      toast.success(`Campo "${fieldData.campo}" criado com sucesso!`, {
        position: 'top-center',
        duration: 2000,
      });
      setNewFieldName('');
      setNewFieldType('text');
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao criar campo. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
    } finally {
      setCreatingField(null);
    }
  };

  const handleCreateCustomField = async () => {
    const trimmedName = newFieldName.trim().replace(/\s+/g, '_');
    if (!trimmedName) {
      toast.error('Digite um nome para o campo.', {
        position: 'top-center',
        duration: 2000,
      });
      return;
    }

    await handleCreateField({ campo: trimmedName, tipo: newFieldType });
  };

  const handleDeleteFieldClick = (field: ICompanyField) => {
    setFieldToDelete(field);
    onDeleteAlertOpen();
  };

  // Deletar somente o campo (mantém valores)
  const handleConfirmDeleteFieldOnly = async () => {
    if (!fieldToDelete) return;

    setDeletingField(fieldToDelete.campo);
    try {
      await deleteFieldMutation(fieldToDelete.campo);
      toast.success(`Campo "${fieldToDelete.campo}" deletado! Recarregando...`, {
        position: 'top-center',
        duration: 2000,
      });
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao deletar campo. Tente novamente.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
      setDeletingField(null);
      setFieldToDelete(null);
      onDeleteAlertClose();
    }
  };

  // Deletar campo E todos os valores (cascade)
  const handleConfirmDeleteCascade = async () => {
    if (!fieldToDelete) return;

    setDeletingField(fieldToDelete.campo);
    try {
      await deleteFieldCascadeMutation(fieldToDelete.campo);
      toast.success(
        `Campo "${fieldToDelete.campo}" e todos os valores foram deletados! Recarregando...`,
        {
          position: 'top-center',
          duration: 2000,
        },
      );
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      const errorMessage = getErrorMessage(
        error,
        'Erro ao deletar campo e valores. Tente novamente.',
      );
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
      setDeletingField(null);
      setFieldToDelete(null);
      onDeleteAlertClose();
    }
  };

  const formatFieldLabel = (campo: string) => {
    return campo.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  };

  // Handler para limpar apenas valores órfãos
  const handleClearOrphanValues = async () => {
    setIsClearingValues(true);
    try {
      await clearOrphanValues();
      toast.success('Valores órfãos removidos com sucesso! Recarregando...', {
        position: 'top-center',
        duration: 2000,
      });
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao remover valores órfãos.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
      setIsClearingValues(false);
    }
  };

  // Handler para limpar apenas sets órfãos
  const handleClearOrphanSets = async () => {
    setIsClearingSets(true);
    try {
      await clearOrphanSets();
      toast.success('Campos órfãos removidos com sucesso! Recarregando...', {
        position: 'top-center',
        duration: 2000,
      });
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao remover campos órfãos.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
      setIsClearingSets(false);
    }
  };

  // Handler para limpar todos os órfãos
  const handleClearAllOrphans = async () => {
    setIsClearingAll(true);
    try {
      await clearAllOrphans();
      toast.success('Todas as atribuições órfãs foram removidas! Recarregando...', {
        position: 'top-center',
        duration: 2000,
      });
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Erro ao remover atribuições órfãs.');
      toast.error(errorMessage, { position: 'top-right', duration: 3000 });
      setIsClearingAll(false);
    }
  };

  const isClearing = isClearingValues || isClearingSets || isClearingAll;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent maxH="90vh">
          <ModalHeader pb={2}>
            <Flex direction="column" gap={1}>
              <Text fontSize="lg" fontWeight="bold">
                Gerenciar Campos
              </Text>
              <Text fontSize="sm" color="gray.500" fontWeight="normal">
                Adicione ou remova campos personalizados
              </Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={0} pb={6}>
            {isLoading ? (
              <Flex justify="center" align="center" py={8}>
                <Spinner size="lg" color="blue.500" />
                <Text ml={3} color="gray.500">
                  Carregando campos...
                </Text>
              </Flex>
            ) : (
              <Tabs isFitted variant="enclosed" colorScheme="blue">
                <TabList mb={4}>
                  <Tab fontWeight="medium">
                    <Icon as={Plus} mr={2} />
                    Criar Campo
                  </Tab>
                  <Tab fontWeight="medium">
                    <Icon as={Trash} mr={2} />
                    Deletar Campos
                  </Tab>
                  <Tab fontWeight="medium">
                    <Icon as={Recycle} mr={2} />
                    Limpeza
                    {totalOrphans > 0 && (
                      <Badge ml={2} colorScheme="orange" borderRadius="full" fontSize="xs">
                        {totalOrphans}
                      </Badge>
                    )}
                  </Tab>
                </TabList>

                <TabPanels>
                  {/* Tab Criar */}
                  <TabPanel p={0}>
                    <VStack spacing={4} align="stretch">
                      {/* Campos Existentes */}
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.600">
                          Campos Existentes ({existingFields.length})
                        </Text>
                        {existingFields.length > 0 ? (
                          <Flex wrap="wrap" gap={2}>
                            {existingFields.map(field => (
                              <Badge
                                key={field.campo}
                                colorScheme="green"
                                px={2}
                                py={1}
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                gap={1}
                                fontSize="xs"
                              >
                                <Icon as={CheckCircle} weight="fill" boxSize={3} />
                                {formatFieldLabel(field.campo)}
                              </Badge>
                            ))}
                          </Flex>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            Nenhum campo cadastrado ainda.
                          </Text>
                        )}
                      </Box>

                      {/* Criar Novo Campo */}
                      <Box p={4} borderRadius="md" border="1px" borderColor="gray.200" bg="gray.50">
                        <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.700">
                          Criar Novo Campo
                        </Text>
                        <VStack spacing={3} align="stretch">
                          <FormControl>
                            <FormLabel fontSize="sm">Nome do Campo</FormLabel>
                            <Input
                              value={newFieldName}
                              onChange={e => setNewFieldName(e.target.value)}
                              placeholder="Ex: nivel, categoria, setor, etc."
                              bg="white"
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Tipo do Campo</FormLabel>
                            <Select
                              value={newFieldType}
                              onChange={e => setNewFieldType(e.target.value as 'select' | 'text')}
                              bg="white"
                            >
                              <option value="text">Texto Livre</option>
                              <option value="select">Seleção</option>
                            </Select>
                          </FormControl>
                          <Button
                            colorScheme="blue"
                            leftIcon={<Icon as={Plus} />}
                            onClick={handleCreateCustomField}
                            isLoading={
                              creatingField ===
                              newFieldName.trim().toLowerCase().replace(/\s+/g, '_')
                            }
                            isDisabled={!newFieldName.trim()}
                          >
                            Criar Campo
                          </Button>
                        </VStack>
                      </Box>
                    </VStack>
                  </TabPanel>

                  {/* Tab Deletar */}
                  <TabPanel p={0}>
                    <VStack spacing={3} align="stretch">
                      <Text fontSize="sm" color="gray.600" mb={2}>
                        Deletar um campo remove permanentemente o campo e todos os valores
                        associados de todas as empresas.
                      </Text>
                      {existingFields.length > 0 ? (
                        existingFields.map(field => (
                          <Flex
                            key={field.campo}
                            p={3}
                            borderRadius="md"
                            border="1px"
                            borderColor="gray.200"
                            bg="white"
                            justify="space-between"
                            align="center"
                          >
                            <Flex align="center" gap={2}>
                              <Text fontWeight="medium">{formatFieldLabel(field.campo)}</Text>
                              <Badge
                                colorScheme={field.tipo === 'select' ? 'blue' : 'purple'}
                                fontSize="xs"
                              >
                                {field.tipo === 'select' ? 'Seleção' : 'Texto'}
                              </Badge>
                            </Flex>
                            <IconButton
                              aria-label="Deletar campo"
                              icon={<Icon as={Trash} />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteFieldClick(field)}
                              isLoading={deletingField === field.campo}
                            />
                          </Flex>
                        ))
                      ) : (
                        <Box py={8} textAlign="center">
                          <Text color="gray.500">Nenhum campo para deletar.</Text>
                        </Box>
                      )}
                    </VStack>
                  </TabPanel>

                  {/* Tab Limpeza - Órfãos */}
                  <TabPanel p={0}>
                    <VStack spacing={4} align="stretch">
                      {/* Card de informação */}
                      <Box
                        p={4}
                        borderRadius="md"
                        bg="orange.50"
                        border="1px"
                        borderColor="orange.200"
                      >
                        <Flex align="center" mb={2}>
                          <Icon as={Warning} color="orange.500" boxSize={5} mr={2} />
                          <Text fontWeight="semibold" color="orange.700">
                            Atribuições Órfãs
                          </Text>
                        </Flex>
                        <Text fontSize="sm" color="gray.600">
                          Quando um campo é excluído, as atribuições (valores e seleções) das
                          empresas ficam órfãs. Use esta ferramenta para limpar esses registros.
                        </Text>
                      </Box>

                      {/* Resumo dos órfãos */}
                      {isLoadingOrphans && (
                        <Flex justify="center" align="center" py={4}>
                          <Spinner size="sm" color="orange.500" mr={2} />
                          <Text fontSize="sm" color="gray.500">
                            Verificando atribuições órfãs...
                          </Text>
                        </Flex>
                      )}

                      {!isLoadingOrphans && totalOrphans === 0 && (
                        <Box
                          p={4}
                          borderRadius="md"
                          bg="green.50"
                          border="1px"
                          borderColor="green.200"
                          textAlign="center"
                        >
                          <Text color="green.700" fontWeight="medium">
                            Nenhuma atribuição órfã encontrada!
                          </Text>
                          <Text fontSize="sm" color="gray.500" mt={1}>
                            Seu sistema está limpo.
                          </Text>
                        </Box>
                      )}

                      {!isLoadingOrphans && totalOrphans > 0 && (
                        <>
                          {/* Estatísticas com botões de limpeza */}
                          <VStack spacing={3} align="stretch">
                            {/* Card Campos Órfãos (Sets) */}
                            <Box
                              p={4}
                              borderRadius="md"
                              border="1px"
                              borderColor="blue.200"
                              bg="blue.50"
                            >
                              <Flex justify="space-between" align="center">
                                <Box>
                                  <Text fontSize="sm" color="gray.600">
                                    Campos órfãos (seleções)
                                  </Text>
                                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                                    {orphanSets.length}
                                  </Text>
                                </Box>
                                <Button
                                  colorScheme="blue"
                                  size="sm"
                                  leftIcon={<Icon as={Trash} />}
                                  onClick={handleClearOrphanSets}
                                  isLoading={isClearingSets}
                                  isDisabled={orphanSets.length === 0 || isClearing}
                                >
                                  Limpar
                                </Button>
                              </Flex>
                            </Box>

                            {/* Card Valores Órfãos */}
                            <Box
                              p={4}
                              borderRadius="md"
                              border="1px"
                              borderColor="green.200"
                              bg="green.50"
                            >
                              <Flex justify="space-between" align="center">
                                <Box>
                                  <Text fontSize="sm" color="gray.600">
                                    Valores órfãos (textos)
                                  </Text>
                                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                                    {orphanValues.length}
                                  </Text>
                                </Box>
                                <Button
                                  colorScheme="green"
                                  size="sm"
                                  leftIcon={<Icon as={Trash} />}
                                  onClick={handleClearOrphanValues}
                                  isLoading={isClearingValues}
                                  isDisabled={orphanValues.length === 0 || isClearing}
                                >
                                  Limpar
                                </Button>
                              </Flex>
                            </Box>
                          </VStack>

                          {/* Botão para limpar tudo */}
                          <Button
                            colorScheme="orange"
                            size="lg"
                            leftIcon={<Icon as={Recycle} />}
                            onClick={handleClearAllOrphans}
                            isLoading={isClearingAll}
                            isDisabled={isClearing}
                            width="100%"
                          >
                            Limpar Tudo ({totalOrphans})
                          </Button>
                        </>
                      )}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Alert de Confirmação de Delete */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Deletar Campo
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>
                Como deseja deletar o campo &quot;
                <strong>{fieldToDelete?.campo}</strong>&quot;?
              </Text>
              <Box
                p={3}
                mb={2}
                borderRadius="md"
                border="1px"
                borderColor="orange.200"
                bg="orange.50"
              >
                <Text fontSize="sm" fontWeight="semibold" color="orange.700" mb={1}>
                  Deletar somente o campo
                </Text>
                <Text fontSize="xs" color="orange.600">
                  O campo será removido, mas os valores já salvos nas empresas serão mantidos.
                </Text>
              </Box>
              <Box p={3} borderRadius="md" border="1px" borderColor="red.200" bg="red.50">
                <Text fontSize="sm" fontWeight="semibold" color="red.700" mb={1}>
                  Deletar campo e valores
                </Text>
                <Text fontSize="xs" color="red.600">
                  O campo e <strong>todos os valores</strong> de <strong>todas as empresas</strong>{' '}
                  serão removidos permanentemente.
                </Text>
              </Box>
            </AlertDialogBody>
            <AlertDialogFooter flexDirection="column" gap={2} alignItems="stretch">
              <Flex gap={2} width="100%">
                <Button
                  colorScheme="orange"
                  onClick={handleConfirmDeleteFieldOnly}
                  flex={1}
                  isLoading={deletingField !== null}
                  size="sm"
                >
                  Somente Campo
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleConfirmDeleteCascade}
                  flex={1}
                  isLoading={deletingField !== null}
                  size="sm"
                >
                  Campo e Valores
                </Button>
              </Flex>
              <Button ref={cancelRef} onClick={onDeleteAlertClose} variant="ghost" size="sm">
                Cancelar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
